<template>
  <div class="oring-assistant__results">
    <div class="oring-assistant__results__upper-part">
      <h3 class="h4">
        {{ $tv('Frontend.Title.Result.ORingAssistant', 'ORingConfiguratorImport') }}
      </h3>
      <div class="oring-assistant__results__upper-part__buttons">
        <div class="oring-assistant__results__upper-part__buttons__left">
          <NuxtLink
            @click="openConstructionPopup('download')"
            class="k-button k-button-md k-button-solid k-button-solid-base download-button"
            :class="{ 'k-disabled': globalLoading }"
          >
            {{ $tv('Frontend.ExpertMode.Download', 'ORingConfiguratorImport') }}
          </NuxtLink>
          <NuxtLink
            @click="openConstructionPopup('send')"
            class="k-button k-button-md k-button-solid k-button-solid-base download-button"
            :class="{ 'k-disabled': globalLoading }"
          >
            {{ $tv('Frontend.ExpertMode.SendPerMail', 'ORingConfiguratorImport') }}
          </NuxtLink>
        </div>
        <div class="design-cockpit-link desktop-only">
          <NuxtLink
            @click="oringStore.transferToDesignCockpit()"
            :to="$globalization.localePath('/oring-calculator/design-cockpit')"
            class="k-button k-button-md k-button-solid k-button-solid-base"
            :class="{ 'k-disabled': globalLoading }"
          >
            {{ $tv('goToDesignCockpit', 'ORingConfigurator') }}
            <ArrowRight class="button__icon tab" />
          </NuxtLink>
        </div>
      </div>
      <ORingAssistantUpperGrid
        v-if="!isLoading"
        :table-columns="upperGridColumns"
        :data-items="upperGridData"
        :is-inch-computed="oringStore.oringCalculatorImperial ? i18n.tv('inch', 'ORingConfigurator') : i18n.tv('mm', 'ORingConfigurator')"
      />
      <div
        class="design-cockpit-link mobile-only"
        v-if="!isLoading"
      >
        <NuxtLink
          @click="oringStore.transferToDesignCockpit()"
          :to="$globalization.localePath('/oring-calculator/design-cockpit')"
          class="k-button k-button-md k-button-solid k-button-solid-base"
          :class="{ 'k-disabled': globalLoading }"
        >
          {{ $tv('goToDesignCockpit', 'ORingConfigurator') }}
          <ArrowRight class="button__icon tab" />
        </NuxtLink>
      </div>
      <div
        v-if="isLoading"
        class="oring-results-loading"
      >
        <AppLoadingIndicator />
      </div>
    </div>
    <ManagementCard class="oring-results-card">
      <h3 class="h4">
        {{ $tv('Frontend.Title.FittingCatalogRings', 'ORingConfiguratorImport') }}
      </h3>
      <div
        v-if="!isLoading"
        class="oring-results-card__description"
      >
        {{ i18n.tv('description', 'ORingConfigurator') }}
      </div>
      <ORingResultsGrid
        v-if="!isLoading"
        :table-columns="tableColumns"
        :data-items="mappedTableData"
        :is-inch-computed="oringStore.oringCalculatorImperial ? 'inch' : 'mm'"
      />
      <div
        v-if="isLoading"
        class="oring-results-loading"
      >
        <AppLoadingIndicator />
      </div>
    </ManagementCard>
    <PopupORingConstruction
      :show="showConstructionPopup"
      :mode="constructionMode"
      :initial-data="constructionFormData"
      :on-submit="onConstructionSubmit"
      @close-pop-up="closeConstructionPopup"
    />
  </div>
</template>
<script setup lang="ts">
import {ref, computed, onMounted} from 'vue'
import ArrowRight from '~/assets/icons/md_arrow_forward.svg?component'
import ORingAssistantUpperGrid from '~/components/oring-calculator/ORingAssistantUpperGrid.vue'
import ORingResultsGrid from '~/components/oring-calculator/ORingResultsGrid.vue'
import PopupORingConstruction from '~/components/popup/PopupORingConstruction.vue'
import AppLoadingIndicator from '~/components/loading-indicators/app-loading-indicator.vue'
import {useOringCalculatorService} from '~/composables/oring_calc_service'
import useI18nExtended from '~/composables/usei18nExtended'
import {useORingCalculatorStore} from '@/store/oring-calculator'
import {handleConstructionSubmit} from '@/composables/oring-helper'
import {storeToRefs} from 'pinia'
import cloneDeep from 'lodash/cloneDeep'

const {calculateOptimalORingAssistant} = useOringCalculatorService()
const oringStore = useORingCalculatorStore()
const i18n = useI18nExtended()

const {oringAssistantOptimalORing, oringCalculatorImperial, oringAssistantCurrentHousing, globalLoading, oringMaterialIds} = storeToRefs(oringStore)
const isLoading = ref(true)

onMounted(async () => {
  globalLoading.value = true
  try {
    oringAssistantOptimalORing.value = await calculateOptimalORingAssistant(oringStore.oringAssistantCalculateOptimalOringRequest())
    if (oringAssistantOptimalORing.value?.assistantORing) {
      oringAssistantCurrentHousing.value = cloneDeep(oringAssistantOptimalORing.value.assistantORing)
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching results:', error)
    oringAssistantOptimalORing.value = {
      assistantORing: null,
      catalogueRings: []
    }
  } finally {
    isLoading.value = false
    globalLoading.value = false
  }
})

const showConstructionPopup = ref(false)
const constructionMode = ref<'download' | 'send'>('download')
const constructionFormData = ref({
  firstName: '',
  lastName: '',
  company: '',
  email: '',
  designRemarks: '',
  pdfLanguage: i18n.locale.value
})

const openConstructionPopup = (mode: 'download' | 'send') => {
  constructionMode.value = mode
  showConstructionPopup.value = true
}

const closeConstructionPopup = () => {
  showConstructionPopup.value = false
}

const onConstructionSubmit = async (submitData: any) => {
  await handleConstructionSubmit({
    mode: submitData.mode,
    popupData: {
      name: submitData.name,
      company: submitData.company,
      email: submitData.email,
      designRemarks: submitData.designRemarks,
      pdfLanguage: submitData.pdfLanguage
    },
    housingType: 'assistant',
    customHousingData: oringAssistantOptimalORing.value?.assistantORing || null
  })
}

const mappedTableData = computed(() => {
  return oringAssistantOptimalORing.value?.catalogueRings?.map((item: any) => {
    const rawMaterial = item.catalogueRing?.materialName || item.oRing?.materialName
    return {
      articleNo: item.catalogueRing?.articleNo || item.oRing?.articleNo,
      productDescription: item.catalogueRing?.productDescription,
      isAvailable: item.catalogueRing?.isAvailable,
      materialName: Array.isArray(rawMaterial) ? rawMaterial[0] : rawMaterial,
      d1: Number(item.catalogueRing?.d1?.val || item.oRing?.d1?.val || 0).toFixed(nrOfDecimalPoints.value),
      d2: Number(item.catalogueRing?.d2?.val || item.oRing?.d2?.val || 0).toFixed(nrOfDecimalPoints.value),
      stretchingMin: item.stretching?.valueMin?.toFixed(1),
      stretchingMean: item.stretching?.valueMean?.toFixed(1),
      stretchingMax: item.stretching?.valueMax?.toFixed(1),
      stretchingMinColorRating: item.stretching?.minColorRating || 0,
      stretchingMaxColorRating: item.stretching?.maxColorRating || 0,
      stretchingMeanColorRating: item.stretching?.meanColorRating || 0,
      holdingRatioMin: item.holdingRatio?.valueMin?.toFixed(1),
      holdingRatioMean: item.holdingRatio?.valueMean?.toFixed(1),
      holdingRatioMax: item.holdingRatio?.valueMax?.toFixed(1),
      holdingRatioMinColorRating: item.holdingRatio?.minColorRating || 0,
      holdingRatioMaxColorRating: item.holdingRatio?.maxColorRating || 0,
      holdingRatioMeanColorRating: item.holdingRatio?.meanColorRating || 0,
      compressionMin: item.compression?.valueMin?.toFixed(1),
      compressionMean: item.compression?.valueMean?.toFixed(1),
      compressionMax: item.compression?.valueMax?.toFixed(1),
      compressionMinColorRating: item.compression?.minColorRating || 0,
      compressionMaxColorRating: item.compression?.maxColorRating || 0,
      compressionMeanColorRating: item.compression?.meanColorRating || 0,
      fillingRatioMin: item.fillingRatio?.valueMin?.toFixed(1),
      fillingRatioMean: item.fillingRatio?.valueMean?.toFixed(1),
      fillingRatioMax: item.fillingRatio?.valueMax?.toFixed(1),
      fillingRatioMinColorRating: item.fillingRatio?.minColorRating || 0,
      fillingRatioMaxColorRating: item.fillingRatio?.maxColorRating || 0,
      fillingRatioMeanColorRating: item.fillingRatio?.meanColorRating || 0
    }}) || []
})

const getMaterialDisplayName = (materialName: string | string[] | null): string[] => {
  if (!oringMaterialIds.value.length || !materialName || materialName.length === 0) {
    return [i18n.tv('notSelected', 'ORingConfigurator')]
  }
  return Array.isArray(materialName) ? materialName : [materialName]
}

const nrOfDecimalPoints = computed(() => {
  return oringCalculatorImperial.value ? 4 : 2
})

const upperGridData = computed(() => {
  return oringAssistantOptimalORing.value?.assistantORing ? [
    {
      materialName: getMaterialDisplayName(oringAssistantOptimalORing.value.assistantORing.oRing?.materialName),
      d1: oringAssistantOptimalORing.value.assistantORing.oRing?.d1?.val.toFixed(nrOfDecimalPoints.value),
      d2: oringAssistantOptimalORing.value.assistantORing.oRing?.d2?.val.toFixed(nrOfDecimalPoints.value),
      stretchingMin: oringAssistantOptimalORing.value.assistantORing.stretching?.valueMin?.toFixed(1),
      stretchingMean: oringAssistantOptimalORing.value.assistantORing.stretching?.valueMean?.toFixed(1),
      stretchingMax: oringAssistantOptimalORing.value.assistantORing.stretching?.valueMax?.toFixed(1),
      stretchingMinColorRating: oringAssistantOptimalORing.value.assistantORing.stretching?.minColorRating || 0,
      stretchingMaxColorRating: oringAssistantOptimalORing.value.assistantORing.stretching?.maxColorRating || 0,
      stretchingMeanColorRating: oringAssistantOptimalORing.value.assistantORing.stretching?.meanColorRating || 0,
      holdingRatioMin: oringAssistantOptimalORing.value.assistantORing.holdingRatio?.valueMin?.toFixed(1),
      holdingRatioMean: oringAssistantOptimalORing.value.assistantORing.holdingRatio?.valueMean?.toFixed(1),
      holdingRatioMax: oringAssistantOptimalORing.value.assistantORing.holdingRatio?.valueMax?.toFixed(1),
      holdingRatioMinColorRating: oringAssistantOptimalORing.value.assistantORing.holdingRatio?.minColorRating || 0,
      holdingRatioMaxColorRating: oringAssistantOptimalORing.value.assistantORing.holdingRatio?.maxColorRating || 0,
      holdingRatioMeanColorRating: oringAssistantOptimalORing.value.assistantORing.holdingRatio?.meanColorRating || 0,
      compressionMin: oringAssistantOptimalORing.value.assistantORing.compression?.valueMin?.toFixed(1),
      compressionMean: oringAssistantOptimalORing.value.assistantORing.compression?.valueMean?.toFixed(1),
      compressionMax: oringAssistantOptimalORing.value.assistantORing.compression?.valueMax?.toFixed(1),
      compressionMinColorRating: oringAssistantOptimalORing.value.assistantORing.compression?.minColorRating || 0,
      compressionMaxColorRating: oringAssistantOptimalORing.value.assistantORing.compression?.maxColorRating || 0,
      compressionMeanColorRating: oringAssistantOptimalORing.value.assistantORing.compression?.meanColorRating || 0,
      fillingRatioMean: oringAssistantOptimalORing.value.assistantORing.fillingRatio?.valueMean?.toFixed(1),
      fillingRatioMax: oringAssistantOptimalORing.value.assistantORing.fillingRatio?.valueMax?.toFixed(1),
      fillingRatioMinColorRating: oringAssistantOptimalORing.value.assistantORing.fillingRatio?.minColorRating || 0,
      fillingRatioMaxColorRating: oringAssistantOptimalORing.value.assistantORing.fillingRatio?.maxColorRating || 0,
      fillingRatioMeanColorRating: oringAssistantOptimalORing.value.assistantORing.fillingRatio?.meanColorRating || 0
    }
  ] : []
})

const upperGridColumns = computed(() => {  const baseColumns = [
  {
    field: '',
    title: '',
    width: 130,
    cell: 'emptyCell',
    className: 'mobile--visible'
  },
  {
    field: '',
    title: '',
    width: 138,
    cell: 'emptyCell',
    className: 'mobile--visible'
  },
  {
    field: 'materialName',
    title: i18n.tv('Frontend.MaterialAssistant.Material', 'ORingConfiguratorImport'),
    width: 138,
    cell: 'materialCell',
    className: 'mobile--visible'
  },
  {
    field: 'd1',
    title: i18n.tv('Attribute.d1', 'ORingConfiguratorImport'),
    width: 90,
    cell: 'dimensionCell',
    headerClassName: 'right-align-header',
    className: 'mobile--visible'
  },
  {
    field: 'd2',
    title: i18n.tv('Attribute.d2', 'ORingConfiguratorImport'),
    width: 90,
    cell: 'dimensionCell',
    headerClassName: 'right-align-header',
    className: 'mobile--visible'
  }
]

const geometryId = oringStore.oringAssistantGeometryId
let conditionalColumns: any[] = []

if (geometryId === 9 || geometryId === 12) {
  conditionalColumns.push({
    field: 'stretching',
    title: i18n.tv('stretching', 'ORingConfigurator'),
    width: geometryId === 12 ? 100 : 138,
    cell: 'stretching',
    minField: geometryId === 12 ? null : 'stretchingMin',
    maxField: 'stretchingMax',
    headerClassName: 'right-align-header',
    className: 'mobile--visible'
  })
}

if (geometryId === 8 || geometryId === 12) {
  conditionalColumns.push({
    field: 'holdingRatio',
    title: i18n.tv('Frontend.Report.HoldingRatio', 'ORingConfiguratorImport'),
    width: geometryId === 12 ? 105 : 138,
    cell: 'stretching',
    minField: geometryId === 12 ? null : 'holdingRatioMin',
    maxField: 'holdingRatioMax',
    headerClassName: 'right-align-header',
    className: 'mobile--visible'
  })
}

const compressionColumn = {
  field: 'compression',
  title: i18n.tv('centricCompression', 'ORingConfigurator'),
  width: 145,
  cell: 'stretching',
  minField: 'compressionMin',
  maxField: 'compressionMax',
  headerClassName: 'right-align-header',
  className: 'mobile--visible'
}
const fillingRatioColumn = {
  field: 'fillingRatio',
  title: i18n.tv('Frontend.Parameter.FillingRatio', 'ORingConfiguratorImport'),
  width: 138,
  cell: 'stretching',
  maxField: 'fillingRatioMax',
  meanField: 'fillingRatioMean',
  headerClassName: 'right-align-header',
  className: 'mobile--visible'
}
const endColumns = [
  {
    field: '',
    title: '',
    width: 110,
    cell: 'emptyCell',
    className: 'mobile--visible'
  }
]

return [...baseColumns, ...conditionalColumns, compressionColumn, fillingRatioColumn, ...endColumns]
})

const tableColumns = computed(() => {
  const baseColumns = [
    {
      field: 'articleNo',
      title: i18n.tv('itemNo', 'cart'),
      width: 130,
      cell: 'sku',
      className: 'cell-with-icon mobile--visible'
    },
    {
      field: 'isAvailable',
      title: i18n.tv('Frontend.Parameter.Availability', 'ORingConfiguratorImport'),
      width: 138,
      cell: 'availability',
      className: 'mobile--visible'
    },
    {
      field: 'materialName',
      title: i18n.tv('Frontend.MaterialAssistant.Material', 'ORingConfiguratorImport'),
      width: 138,
      cell: 'simpleCell',
      className: 'mobile--visible'
    },
    {
      field: 'd1',
      title: i18n.tv('Attribute.d1', 'ORingConfiguratorImport'),
      width: 90,
      cell: 'dimensionCell',
      headerClassName: 'right-align-header',
      className: 'mobile--visible'
    },
    {
      field: 'd2',
      title: i18n.tv('Attribute.d2', 'ORingConfiguratorImport'),
      width: 90,
      cell: 'dimensionCell',
      headerClassName: 'right-align-header',
      className: 'mobile--visible'
    }
  ]
  const geometryId = oringStore.oringAssistantGeometryId
  let conditionalColumns: any[] = []

  if (geometryId === 9 || geometryId === 12) {
    conditionalColumns.push({
      field: 'stretching',
      title: i18n.tv('stretching', 'ORingConfigurator'),
      width: geometryId === 12 ? 100 : 138,
      cell: 'stretching',
      minField: geometryId === 12 ? null : 'stretchingMin',
      maxField: 'stretchingMax',
      headerClassName: 'right-align-header',
      className: 'mobile--visible'
    })
  }

  if (geometryId === 8 || geometryId === 12) {
    conditionalColumns.push({
      field: 'holdingRatio',
      title: i18n.tv('Frontend.Report.HoldingRatio', 'ORingConfiguratorImport'),
      width: geometryId === 12 ? 105 : 138,
      cell: 'stretching',
      minField: geometryId === 12 ? null : 'holdingRatioMin',
      maxField: 'holdingRatioMax',
      headerClassName: 'right-align-header',
      className: 'mobile--visible'
    })
  }
  const compressionColumn = {
    field: 'compression',
    title: i18n.tv('centricCompression', 'ORingConfigurator'),
    width: 145,
    cell: 'stretching',
    minField: 'compressionMin',
    maxField: 'compressionMax',
    headerClassName: 'right-align-header',
    className: 'mobile--visible'
  }
  const fillingRatioColumn = {
    field: 'fillingRatio',
    title: i18n.tv('Frontend.Parameter.FillingRatio', 'ORingConfiguratorImport'),
    width: 138,
    cell: 'stretching',
    maxField: 'fillingRatioMax',
    meanField: 'fillingRatioMean',
    headerClassName: 'right-align-header',
    className: 'mobile--visible'
  }
  const endColumns = [
    {
      field: '',
      title: i18n.tv('detailsPage', 'ORingConfigurator'),
      width: 110,
      cell: 'detailsPage',
      className: 'mobile--visible'
    }
  ]

  return [...baseColumns, ...conditionalColumns, compressionColumn, fillingRatioColumn, ...endColumns]
})

</script>
<style lang="scss">
.oring-assistant__results {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__upper-part {
    display: flex;
    flex-direction: column;
    background-color: $color__fst__blue__010;
    padding: 1rem;
    gap: 1rem;
    overflow-x: auto;

    h3 {
      margin: 0 0 .125rem;
    }

    &__buttons {
      display: flex;
      justify-content: space-between;

      &__left {
        display: flex;
        gap: 1rem;

        @include breakpoint-down($sm) {
          width: 100%;

          .download-button {
            flex-grow: 1;
          }
        }

        .button__icon {
          width: 1.2rem;
          height: 1.2rem;
        }
      }

        .icon.icon--svg {
          width: 1.2rem;
          height: 1.2rem;
        }

        @include breakpoint-down($sm) {
          display: flex;
          flex-direction: column;
        }
    }

     .k-grid .k-table-th {
        border: 0;
        padding-bottom: 0;
        padding-top: 0;
        font-weight: $base-text-font-weight-light;
      }

      .k-grid td {
        border: none;
        vertical-align: baseline;
      }

      .k-grid-header .k-header.right-align-header {
        justify-content: flex-end;
      }

      .k-grid .k-table-tbody>.k-table-row:not(.k-detail-row):hover {
        background-color: $color__fst__blue__010;
      }
  }

  .oring-results-card {
    margin: 0 0 1rem;
    overflow-x: auto;

    .h4 {
      margin: 0 0 1rem;

      @include breakpoint-down($md) {
        margin: 0 -1rem 1rem;
      }
    }

    &__description {
      margin: 0 0 1rem;
      font-size: 0.875rem;
      color: $color__fst__primary;
      font-weight: $base-text-font-weight-light;

      @include breakpoint-down($md) {
        margin: 0 -1rem 1rem;
      }
    }

    .oring-results-loading {
      min-height: 12.5rem;
    }
  }

  .oring-results-loading {
    display: flex;
    justify-content: center;
    align-items: center;
  }

   @include breakpoint-down($xl) {
    .oring-assistant__results__upper-part,
    .oring-results-card {
      overflow-x: auto;
    }
  }

   @include breakpoint-down($sm) {
    .oring-assistant__results__upper-part,
    .oring-results-card {
      overflow-x: hidden;
    }
  }

  @include breakpoint-down($sm) {
    .management-card {
      box-shadow: none;
    }

  }
}

.mobile-only { display: none }
.desktop-only { display: block }

@include breakpoint-down($sm) {
  .mobile-only {
     display: flex;
     width: 100%;
  }

  .desktop-only {
    display: none;
  }

  .oring-assistant__results__upper-part {
      overflow-x: hidden;
    }

  // Make the Design Cockpit button full-width on mobile
  .oring-assistant__results .design-cockpit-link.mobile-only .k-button {
    width: 100%;
  }
}
</style>
